import { html } from 'lit';
import handshakeBackdrop from '../../img/hero/company-handshake.webp';
import { applicationPortal, type LandingContent } from '../../shared/content/landingContent.js';
import { combineHeadingTitle, splitHeadingHighlight } from '../components/highlightedHeading.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import { editorNodeClass, editorNodeClick, editorNodePointerDown, editorNodeStyle, type LandingEditorRenderOptions } from '../editor/landingEditorRender.js';
import { openCompanyContactDialog } from './navigation.js';

export const companyPlatformHeroSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => {
  const hero = content.companyPlatformHero;
  const backdropImage = hero.backgroundImage ?? handshakeBackdrop;
  const backdropId = 'companyPlatformHero.backgroundImage';
  const hasBackdrop = Boolean(backdropImage) && !editor?.project.elementFrames[backdropId]?.hidden;
  const parts = splitHeadingHighlight(combineHeadingTitle(hero.title, hero.highlight), hero.highlight);
  const text = (field: string, value: string, tag: 'p' | 'h2' | 'h3' = 'p', className = '') => editorSemanticText({ editor, elementId: `companyPlatformHero.${field}`, fallbackTag: tag, className, content: value });
  const targetFor = (field: 'primaryAction' | 'secondaryAction') =>
    field === 'primaryAction' && hero[field].href === '#matching-demo' ? applicationPortal.overview : hero[field].href;
  const enesPortrait = hero.portraits.second;
  const enesPortraitId = 'companyPlatformHero.portraits.second.image';
  // Shared grid rows preserve the staggered bubbles with longer translations and custom portrait sizes.
  const hasPortrait = Boolean(enesPortrait.image) && !editor?.project.elementFrames[enesPortraitId]?.hidden;
  const enesImagePointerDown = (event: PointerEvent) => {
    if (editor?.mode !== 'editor') return;
    event.preventDefault();
    event.stopPropagation();
    editor.onElementSelect?.(enesPortraitId);
    editor.onElementPointerDown?.(event, enesPortraitId);
  };
  const enesImageClick = (event: MouseEvent) => {
    if (editor?.mode === 'editor') {
      event.preventDefault();
      event.stopPropagation();
      editorNodeClick(editor, enesPortraitId, event);
      return;
    }
    action(event, 'primaryAction');
  };
  const action = (event: MouseEvent, field: 'primaryAction' | 'secondaryAction') => {
    if (editor?.mode === 'editor') {
      event.preventDefault();
      editorNodeClick(editor, `companyPlatformHero.${field}.label`, event);
      return;
    }
    const target = targetFor(field);
    if (target === '#kontakt') {
      event.preventDefault();
      openCompanyContactDialog((event.currentTarget as HTMLElement).closest('.vp-page'));
    } else if (target === '#matching-demo') {
      const demo = (event.currentTarget as HTMLElement).closest('.vp-page')?.querySelector('vp-company-platform-hero');
      if (demo) {
        event.preventDefault();
        demo.dispatchEvent(new CustomEvent('open-matching-demo', { detail: { instant: event.detail === 0 } }));
      }
    } else if (target && event.currentTarget instanceof HTMLButtonElement) {
      window.location.assign(target);
    }
  };
  return html`
    <section
      class=${editorNodeClass(editor, 'section.companyPlatformHero',
        `vp-company-hero${hasBackdrop ? ' has-backdrop' : ''}${editor?.mode === 'editor' ? ' is-editor' : ''}`)}
      style=${editorNodeStyle(editor, 'section.companyPlatformHero')}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'section.companyPlatformHero', event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, 'section.companyPlatformHero', event)}>
      <div class="vp-company-hero-backdrop" aria-hidden="true">
        ${backdropImage ? html`<img src=${backdropImage} alt="" width="1536" height="1024" loading="eager" fetchpriority="high" decoding="async"
          class=${editorNodeClass(editor, backdropId, 'vp-company-hero-handshake')} style=${editorNodeStyle(editor, backdropId)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, backdropId, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, backdropId, event)} />` : ''}
      </div>
      <div class="vp-company-hero-grid">
        <div class=${editorNodeClass(editor, 'companyPlatformHero.copy', 'vp-company-hero-copy')} style=${editorNodeStyle(editor, 'companyPlatformHero.copy')}>
          ${editorSemanticText({ editor, elementId: 'companyPlatformHero.title', fallbackTag: 'h1', className: 'vp-company-hero-title vp-highlight-heading', content: html`${parts.before}<span class=${editorNodeClass(editor, 'companyPlatformHero.highlight', 'vp-inline-text-highlight')} style=${editorNodeStyle(editor, 'companyPlatformHero.highlight')} @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'companyPlatformHero.highlight', event)} @click=${(event: MouseEvent) => editorNodeClick(editor, 'companyPlatformHero.highlight', event)}>${parts.highlight}</span>${parts.after}` })}
          <figure class=${editorNodeClass(editor, 'companyPlatformHero.portraits', 'vp-company-hero-portraits')} style=${editorNodeStyle(editor, 'companyPlatformHero.portraits')}>
            <div class=${`vp-company-hero-cta-layout${hasPortrait ? '' : ' is-without-portrait'}`}>
              ${text('intro', hero.intro, 'p', 'vp-company-hero-intro')}
              ${hasPortrait ? html`
                <button type="button" class="vp-company-hero-portrait-button"
                  aria-label=${hero.primaryAction.label} @pointerdown=${enesImagePointerDown} @click=${enesImageClick}>
                  <img src=${enesPortrait.image} alt=${enesPortrait.imageAlt} width="400" height="500" loading="eager" decoding="async"
                    class=${editorNodeClass(editor, enesPortraitId, 'vp-company-hero-portrait')} style=${editorNodeStyle(editor, enesPortraitId)} />
                </button>
              ` : ''}
              <div class=${editorNodeClass(editor, 'companyPlatformHero.actions', 'vp-company-hero-actions')} style=${editorNodeStyle(editor, 'companyPlatformHero.actions')}>
                ${(['secondaryAction', 'primaryAction'] as const).map(field => html`
                  <a href=${targetFor(field)} aria-label=${hero[field].label} data-vp-company-hero-action=${field}
                    class=${`vp-action ${field === 'primaryAction' ? 'vp-action-primary' : 'vp-action-secondary'}`}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `companyPlatformHero.${field}.label`, event)}
                    @click=${(event: MouseEvent) => action(event, field)}>
                    ${text(`${field}.label`, hero[field].label, 'p', 'vp-company-hero-speech-label')}
                    <span class="vp-company-hero-speech-arrow" aria-hidden="true">→</span>
                  </a>
                `)}
              </div>
            </div>
          </figure>
        </div>
        <div class=${editorNodeClass(editor, 'companyPlatformHero.visual', 'vp-company-hero-visual')} style=${editorNodeStyle(editor, 'companyPlatformHero.visual')}>

        <ol class=${editorNodeClass(editor, 'companyPlatformHero.benefits', 'vp-company-hero-benefits')} style=${editorNodeStyle(editor, 'companyPlatformHero.benefits')}>
          ${hero.benefits.map((benefit, index) => html`
            <li class=${editorNodeClass(editor, `companyPlatformHero.benefits.${index}`, 'vp-company-benefit')} style=${editorNodeStyle(editor, `companyPlatformHero.benefits.${index}`)}>
              <span class="vp-company-benefit-number" aria-hidden="true">${String(index + 1).padStart(2, '0')}</span>
              <div>${text(`benefits.${index}.title`, benefit.title, 'h2')}${text(`benefits.${index}.text`, benefit.text)}</div>
            </li>
          `)}
        </ol>
        </div>
      </div>
    </section>
  `;
};
